/**
 * 园区数据加载与筛选 Hook
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { fetchAllParks } from '../api/parks';
import {
  DEFAULT_PARKS_DATA_FILTER,
  NationalPark,
  Park,
  ParksDataFilter,
  ProvincialLikePark,
} from '../types';
import {
  countParksByLevel,
  filterParksByCondition,
  parksDataFilterToFilterCondition,
} from '../utils/helpers';

export interface UseParksResult {
  /** 当前筛选结果（合并后的列表） */
  parks: Park[];
  /** 未筛选的全部园区 */
  allParks: Park[];
  loading: boolean;
  /** 加载或解析失败时的错误信息 */
  error: string | null;
  filter: ParksDataFilter;
  setFilter: Dispatch<SetStateAction<ParksDataFilter>>;
  /** 当前结果集中国家级 / 省级数量 */
  statistics: {
    national: number;
    provincial: number;
    pending: number;
  };
}

/**
 * 加载 public/data 下 CSV（经 api 层），管理筛选状态并返回筛选后的结果与统计。
 */
export function useParks(): UseParksResult {
  const [national, setNational] = useState<NationalPark[]>([]);
  const [provincial, setProvincial] = useState<ProvincialLikePark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ParksDataFilter>(DEFAULT_PARKS_DATA_FILTER);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { national: n, provincial: p } = await fetchAllParks();
      setNational(n);
      setProvincial(p);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : '园区数据加载失败，请稍后重试';
      setError(message);
      setNational([]);
      setProvincial([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const allParks = useMemo((): Park[] => {
    return [...national, ...provincial];
  }, [national, provincial]);

  const parks = useMemo<Park[]>(() => {
    const condition = parksDataFilterToFilterCondition(filter);
    return filterParksByCondition(allParks, condition);
  }, [allParks, filter]);

  const statistics = useMemo(() => countParksByLevel(parks), [parks]);

  return {
    parks,
    allParks,
    loading,
    error,
    filter,
    setFilter,
    statistics,
  };
}
